<!-- @format -->

<template>
    <AddHotelReservation
        :value="selectedHotelReservation"
        :event="event"
        :init-from-query="initFromQuery"
        :mobile-hotel-details-class="hotelDetailsClass"
        :start-date-time="startDateReservation"
        :end-date-time="endDateReservation"
        :default-view="defaultView"
        :quantity="2"
        :in-location="currentLocation"
        :distance-unit-of-measure="distanceUnitOfMeasure"
        :bundle-products="bundleProducts"
        :highlighted-item="highlightedHotelReservation"
        :limit="hotelsLimit"
        :range="hotelsRange"
        :read-only-filters="isPreAllocatedFlow"
        :load-only-first-limit="isPreAllocatedFlow"
        suppress-auto-highlight
        :allow-toggle-view="!$vuetify.breakpoint.mdAndUp"
        @change-show-filter="$emit('change-show-filter', $event)"
        @highlight-item="$emit('highlight-hotel-reservation', $event)"
        @input="onInputDo"
        @hotels-loaded="$emit('hotels-loaded', $event)" />
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { getDistanceUnitOfMeasureForCountry } from '@core/utils/measureUtils';
import { getQueryParamsCurrent } from '@core/utils/windowLocationUtils.js';
import { create as CreateBundleProductsModel } from '@tenant/app/modelsV2/BundleProductsModel';
import { validateSearchHotelsQueryParams } from '@tenant/app/utils/search/validateQuery';
import EventFlowMixin from '@tenant/app/mixins/EventFlowMixin';
import hotelsConstants from '@tenant/app/utils/constants/hotels';
import AddHotelReservation from '@tenant/app/modules/hotel/features/addHotelReservation/AddHotelReservation';
import { defaultCheckOutDate } from '@tenant/app/utils/bookingUtils';
import FilterMixin from '@tenant/app/modules/hotel/features/addHotelReservation/AddHotelReservation/FilterMixin';

export default {
    name: 'EventHotels',
    components: {
        AddHotelReservation,
    },
    mixins: [EventFlowMixin, FilterMixin],
    props: {
        event: {
            type: Object,
            required: true,
        },
        isEventBannerVisible: {
            type: Boolean,
            default: true,
        },
        selectedHotelReservation: {
            type: Object,
            default: null,
        },
        busEventsParent: {
            type: Object,
            required: true,
        },
        bundleProducts: {
            type: Object,
            default: function () {
                return CreateBundleProductsModel();
            },
        },
        highlightedHotelReservation: {
            type: Object,
            default: null,
        },
        isNoEvent: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState({
            hotels: (state) => state.addHotelReservationState.hotels.list,
        }),
        ...mapGetters({
            // get location from the event. if event is unknown, the location will have
            // null properties and trigger search location modal
            eventLocation: `events/getEventLocation`,
            isEventUnknown: 'events/isUnknown',
            searchedLocation: 'addHotelReservationState/hotels/getLocation',
            hasCurrentLocation: 'addHotelReservationState/hasCurrentLocation',
        }),
        currentLocation() {
            return this.hasCurrentLocation ? this.searchedLocation : this.eventLocation;
        },
        currency() {
            return this.event.currency;
        },
        startDateReservation() {
            return this.event.startDateTime || this.minBookingStartDate;
        },
        endDateReservation() {
            const { startDateTime, endDateTime } = this.event;
            const usedDate = endDateTime || startDateTime || this.minBookingStartDate;
            return defaultCheckOutDate(
                usedDate,
                this.currentLocation?.city,
                this.currentLocation?.stateCode,
                this.currentLocation?.countryCode
            );
        },
        distanceUnitOfMeasure() {
            return getDistanceUnitOfMeasureForCountry(this.currentLocation.countryCode);
        },
        hotelDetailsClass() {
            return this.isEventBannerVisible ? 'v-dialog__height-80' : 'v-dialog__height-99';
        },
        hotelsLimit() {
            return hotelsConstants.HOTEL_ONLY.limit;
        },
        hotelsRange() {
            return hotelsConstants.HOTEL_ONLY.range;
        },
        initFromQuery() {
            return (
                validateSearchHotelsQueryParams(this.$route.query, {
                    minStartDate: this.minBookingStartDate,
                }) && this.isNoEvent
            );
        },
        hotelsViews() {
            return hotelsConstants.VIEWS;
        },
        defaultView() {
            return this.$vuetify.breakpoint.mdAndUp ? this.hotelsViews.list : this.hotelsViews.map;
        },
    },
    watch: {
        hotels: {
            handler: function (newValue) {
                if (newValue.length) {
                    this.$nextTick(() => {
                        const queryParamsHotelId = getQueryParamsCurrent()?.hotelId;
                        const hotel =
                            queryParamsHotelId &&
                            this.hotels.length <= hotelsConstants.HOTEL_ONLY.limit
                                ? this.hotels.find(
                                      (item) => item.id.toString() === queryParamsHotelId.toString()
                                  )
                                : null;

                        if (hotel) {
                            this.$emit('highlight-hotel-reservation', hotel);
                        }
                    });
                }
            },
        },
    },
    methods: {
        onInputDo(item) {
            this.$emit('select-hotel-reservation', item);
            this.$emit(
                'highlight-hotel-reservation',
                item ? item.item : this.highlightedHotelReservation
            );
        },
    },
};
</script>
